import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Modal,
  type SelectOption,
  TextAreaField,
} from '@skupreme/shared/ui';
import { FormItem, FormSelect, FormTextField } from '@skupreme/shared/ui/form';
import { LoaderBar } from '@components/LoaderBar';
import { ToastMessage } from '@utility/toast';
import { ErrorMessageConstant } from '@utility/constants/common';
import BatchOrdersService from '@utility/services/batch-order';
import { DateUtility } from '@utility/date';
import { CommonUtility } from '@utility/common';
import { useProviderHierarchy } from '@features/storage-providers';
import { marketplaceRegionHook } from '@hooks/marketplace-region';

const batchSchema = z.object({
  name: z.string().trim().min(1, 'Name is a required field'),
  fulfillment_provider_id: z
    .string({ required_error: 'Fulfillment Provider is a required field' })
    .min(1, 'Fulfillment Provider is a required field'),
  memo: z.string().optional(),
});

type BatchFormValues = z.infer<typeof batchSchema>;

type ProviderRow = {
  _id: string;
  name?: string;
  integration_type?: string;
};

type BatchToEdit = {
  _id?: string;
  name?: string;
  memo?: string;
  fulfillment_provider_id?: {
    _id?: string;
    marketplace_region_id?: string;
  };
};

type CreateBatchPopupProps = {
  /** Order id → the fulfillment provider id it is assigned to. */
  selectedOrders: Record<string, string>;
  open: boolean;
  /** `result` is truthy when something was saved; `orderUpdate` false skips refetching orders. */
  closeModal: (result?: unknown, orderUpdate?: boolean) => void;
  orders?: Array<{
    _id: string;
    marketplace?: { region_name?: string };
    shipping_address?: { countryCode?: string };
  }>;
  batchToEdit?: BatchToEdit | null;
};

const FORM_ID = 'create-batch-form';

export function CreateBatchPopup({
  selectedOrders,
  open,
  closeModal,
  orders,
  batchToEdit,
}: CreateBatchPopupProps) {
  const isUpdate = useMemo(
    () => CommonUtility.isValidObject(batchToEdit),
    [batchToEdit],
  );

  const [region, setRegion] = useState('');
  const { data: regionData, loading: regionLoading } = marketplaceRegionHook();
  const { data, loading } = useProviderHierarchy(region, false);
  const [saving, setSaving] = useState(false);

  const fpOptions = useMemo<SelectOption[]>(() => {
    const providers: ProviderRow[] = CommonUtility.isValidArray(data)
      ? data
      : [];
    return providers.map(provider => ({
      value: provider._id,
      label: provider.name ?? '',
      trailing: provider.integration_type ? (
        <span className="sk:text-fg-tertiary">
          {CommonUtility.toTitleCase(provider.integration_type)}
        </span>
      ) : undefined,
    }));
  }, [data]);

  const { control, handleSubmit, reset } = useForm<BatchFormValues>({
    resolver: zodResolver(batchSchema),
    mode: 'all',
    shouldUnregister: true,
  });

  useEffect(() => {
    if (isUpdate) {
      setRegion(
        batchToEdit?.fulfillment_provider_id?.marketplace_region_id ?? '',
      );
    }
  }, [selectedOrders, isUpdate]);

  useEffect(() => {
    if (!isUpdate) {
      const orderIds = Object.keys(selectedOrders);
      const oneOfTheOrder = orders?.find(x => orderIds.includes(x._id));
      const orderRegion =
        oneOfTheOrder?.marketplace?.region_name ||
        oneOfTheOrder?.shipping_address?.countryCode ||
        'US';
      const regionId = regionData.find(x => x.name === orderRegion)?.value;
      setRegion(regionId ?? '');
    }
  }, [regionData, isUpdate, selectedOrders]);

  useEffect(() => {
    if (open) {
      if (isUpdate) {
        reset({
          name: batchToEdit?.name,
          fulfillment_provider_id: batchToEdit?.fulfillment_provider_id?._id,
          memo: batchToEdit?.memo,
        });
      } else {
        reset({
          name: `Batch ${DateUtility.localTZFormatDate(new Date())}`,
          fulfillment_provider_id: Object.values(selectedOrders)?.[0],
          memo: '',
        });
      }
    }
  }, [open, isUpdate]);

  const close = (result?: unknown) => {
    reset({});
    closeModal(result);
  };

  const onCreate = async (values: BatchFormValues, testMode = false) => {
    try {
      setSaving(true);
      const orderIds = Object.keys(selectedOrders);
      /* The batch ships with the provider its orders were assigned, not the one picked here. */
      const reqData = {
        ...values,
        fulfillment_provider_id: selectedOrders[orderIds[0]],
        orders: orderIds,
        ...(testMode ? { mode: 'test' } : {}),
      };

      const result = await BatchOrdersService.add(reqData);
      close(result.data);

      ToastMessage.success(result.data);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(false);
    }
  };

  const onUpdate = async (values: BatchFormValues) => {
    try {
      setSaving(true);
      await BatchOrdersService.update(batchToEdit?._id, values);

      ToastMessage.success('Batch Updated Successfully');
      closeModal(true, false);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const providersLoading = isUpdate ? loading : regionLoading || loading;

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && close()}
      title={isUpdate ? 'Update Batch' : 'Create Batch'}
      size="md"
      showCloseButton
      bodyClassName="sk:relative"
      footer={
        <>
          <Button variant="pill" onPress={() => close()}>
            Cancel
          </Button>
          {isUpdate ? (
            <Button
              variant="primary"
              type="submit"
              form={FORM_ID}
              isDisabled={saving}
            >
              Update
            </Button>
          ) : (
            <>
              <Button
                variant="primary"
                type="submit"
                form={FORM_ID}
                isDisabled={saving}
              >
                Create
              </Button>
              {!CommonUtility.isProductionEnv() && (
                <Button
                  variant="pill"
                  isDisabled={saving}
                  onPress={() =>
                    handleSubmit(values => onCreate(values, true))()
                  }
                >
                  Create Test Batch
                </Button>
              )}
            </>
          )}
        </>
      }
    >
      {saving && <LoaderBar content="Saving" />}
      <form
        id={FORM_ID}
        autoComplete="off"
        noValidate
        className="sk:flex sk:flex-col sk:gap-4"
        onSubmit={handleSubmit(values =>
          isUpdate ? onUpdate(values) : onCreate(values),
        )}
      >
        <FormTextField control={control} name="name" label="Name" isRequired />
        <FormSelect
          control={control}
          name="fulfillment_provider_id"
          label="Fulfillment Provider"
          options={fpOptions}
          placeholder={providersLoading ? 'Loading…' : 'Select'}
          isRequired
        />
        <Controller
          control={control}
          name="memo"
          render={({ field }) => (
            <FormItem label="Memo">
              <TextAreaField
                label="Memo"
                rows={3}
                name={field.name}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            </FormItem>
          )}
        />
      </form>
    </Modal>
  );
}
